import { X } from 'lucide-react';

interface ScanProcessingProps {
  shot: string | null;
  onCancel: () => void;
}

/**
 * Tela de processamento OCR.
 * Background: foto capturada com overlay escuro 70%.
 * Dot pulsing mint + texto de feedback.
 */
export function ScanProcessing({ shot, onCancel }: ScanProcessingProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Lendo o documento"
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#0b0d0c',
        overflow: 'hidden',
      }}
    >
      {/* Imagem capturada como fundo */}
      {shot && (
        <img
          src={shot}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}

      {/* Overlay escuro 70% */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.70)',
        }}
      />

      {/* Botão cancelar topo */}
      <div
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '0 18px',
          zIndex: 10,
        }}
      >
        <button
          onClick={onCancel}
          aria-label="Cancelar leitura"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            borderRadius: 'var(--r-pill)',
            background: 'rgba(255,255,255,0.14)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <X size={14} strokeWidth={2} aria-hidden />
          Cancelar
        </button>
      </div>

      {/* Centro: dots + texto */}
      <div
        style={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 22,
          zIndex: 5,
        }}
      >
        {/* Dot pulsing mint */}
        <div
          aria-hidden="true"
          style={{ display: 'flex', gap: 10 }}
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="ocr-dot"
              style={{ animationDelay: `${i * 0.22}s` }}
            />
          ))}
        </div>

        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '-0.01em',
            }}
          >
            Lendo o documento…
          </div>
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--mint-deep)',
              fontFamily: 'var(--font-mono)',
              marginTop: 6,
              opacity: 0.9,
            }}
          >
            OCR · Claude Vision
          </div>
        </div>
      </div>

      <style>{`
        .ocr-dot {
          display: inline-block;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: var(--mint);
          animation: dot-pulse 1.2s ease-in-out infinite both;
        }
        @keyframes dot-pulse {
          0%, 80%, 100% { transform: scale(0.5); opacity: 0.4; }
          40%            { transform: scale(1.1); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .ocr-dot { animation: none; opacity: 0.8; }
        }
      `}</style>
    </div>
  );
}
